import { useCallback, useRef } from 'react';
import { clamp } from '../../core/math';

export interface RangeSliderProps {
  value: [number, number];
  onChange: (value: [number, number]) => void;
  min: number;
  max: number;
  step?: number;
  scale?: 'linear' | 'log';
  /** Draws a tick at the currently configured single value, for the bulk-range popover. */
  marker?: number;
  disabled?: boolean;
  height?: number;
}

type Handle = 'lo' | 'hi' | 'band';

/**
 * Double-ended slider.
 *
 * Serves two purposes: the `range` parameter kind (a min/max pair that *is* the value,
 * such as a radius band), and the exploration range popover, where it defines the
 * bounds randomise and the batch engine may draw from. In the latter case `marker`
 * shows where the currently set single value sits inside the chosen range.
 */
export function RangeSlider({
  value,
  onChange,
  min,
  max,
  step = 0.01,
  scale = 'linear',
  marker,
  disabled = false,
  height = 20,
}: RangeSliderProps): JSX.Element {
  const trackRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ handle: Handle; startX: number; startLo: number; startHi: number; width: number } | null>(null);

  const toNorm = useCallback(
    (v: number): number => {
      if (scale === 'log') {
        const lo = Math.log(Math.max(1e-6, min));
        const hi = Math.log(Math.max(1e-6, max));
        return clamp((Math.log(Math.max(1e-6, v)) - lo) / (hi - lo), 0, 1);
      }
      return clamp((v - min) / (max - min), 0, 1);
    },
    [min, max, scale],
  );

  const fromNorm = useCallback(
    (t: number): number => {
      const c = clamp(t, 0, 1);
      const raw =
        scale === 'log'
          ? Math.exp(Math.log(Math.max(1e-6, min)) + (Math.log(Math.max(1e-6, max)) - Math.log(Math.max(1e-6, min))) * c)
          : min + (max - min) * c;
      const snapped = Math.round(raw / step) * step;
      return clamp(Number(snapped.toFixed(6)), min, max);
    },
    [min, max, step, scale],
  );

  const loNorm = toNorm(value[0]);
  const hiNorm = toNorm(value[1]);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (disabled) return;
    const track = trackRef.current;
    if (!track) return;
    const rect = track.getBoundingClientRect();
    const t = (e.clientX - rect.left) / rect.width;

    // Pick the nearest handle, unless the press landed comfortably inside the band.
    const dLo = Math.abs(t - loNorm);
    const dHi = Math.abs(t - hiNorm);
    const inside = t > loNorm + 0.04 && t < hiNorm - 0.04;
    const handle: Handle = inside ? 'band' : dLo <= dHi ? 'lo' : 'hi';

    e.currentTarget.setPointerCapture(e.pointerId);
    document.body.classList.add('dragging');
    drag.current = { handle, startX: e.clientX, startLo: value[0], startHi: value[1], width: rect.width };

    if (handle === 'lo') onChange([Math.min(fromNorm(t), value[1]), value[1]]);
    else if (handle === 'hi') onChange([value[0], Math.max(fromNorm(t), value[0])]);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>): void => {
    const d = drag.current;
    if (!d) return;
    const sensitivity = e.shiftKey ? 0.1 : e.altKey ? 10 : 1;
    const dt = ((e.clientX - d.startX) / d.width) * sensitivity;

    if (d.handle === 'band') {
      // Shift the whole window, keeping its width until it hits a bound.
      const widthNorm = toNorm(d.startHi) - toNorm(d.startLo);
      const loT = clamp(toNorm(d.startLo) + dt, 0, 1 - widthNorm);
      onChange([fromNorm(loT), fromNorm(loT + widthNorm)]);
      return;
    }
    if (d.handle === 'lo') {
      const next = fromNorm(toNorm(d.startLo) + dt);
      onChange([Math.min(next, value[1]), value[1]]);
    } else {
      const next = fromNorm(toNorm(d.startHi) + dt);
      onChange([value[0], Math.max(next, value[0])]);
    }
  };

  const endDrag = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (!drag.current) return;
    drag.current = null;
    document.body.classList.remove('dragging');
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
  };

  return (
    <div
      ref={trackRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      className="relative cursor-ew-resize touch-none select-none"
      style={{ height, opacity: disabled ? 0.4 : 1 }}
    >
      <div className="absolute inset-x-0 top-1/2 h-[3px] -translate-y-1/2 rounded-full bg-ink-800" />
      <div
        className="absolute top-1/2 h-[3px] -translate-y-1/2 rounded-full bg-accent-500/70"
        style={{ left: `${loNorm * 100}%`, width: `${Math.max(0, hiNorm - loNorm) * 100}%` }}
      />
      {marker !== undefined && (
        <div
          className="pointer-events-none absolute top-1/2 h-[13px] w-[2px] -translate-x-1/2 -translate-y-1/2 rounded bg-ink-100"
          style={{ left: `${toNorm(marker) * 100}%` }}
          title={`Aktueller Wert: ${marker}`}
        />
      )}
      {([['lo', loNorm], ['hi', hiNorm]] as const).map(([key, norm]) => (
        <div
          key={key}
          className="absolute top-1/2 h-[11px] w-[11px] -translate-x-1/2 -translate-y-1/2 rounded-full
                     border border-ink-900 bg-ink-200 shadow-sm"
          style={{ left: `${norm * 100}%` }}
        />
      ))}
    </div>
  );
}
